/**
 * FILE: packages/supabase/src/services/staff.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import { callHubStaffEdgeFunction } from '../internal/hubEdgeFunction'
import { logActivity, type ActivityActor, withActivityActor } from '../internal/activity'
import type { Database } from '../types'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";

type StaffRow = Database['public']['Tables']['staff']['Row']
export interface AuthUserLookup {
  id: string
  email?: string | null
}

export interface StaffAuthBundle {
  staff: StaffRow
  permissions: string[]
  roleName: string
}

export async function getStaffAuthBundle(email: string, client: DbClient = defaultDbClient): Promise<StaffAuthBundle> {
  // Step 1: fetch staff row
  const { data: staffRow, error: staffError } = await client
    .from('staff')
    .select('*')
    .eq('email', email.trim().toLowerCase())
    .single()

  if (staffError || !staffRow) {
    throw new Error('Staff account not found. Contact super_admin.')
  }

  // Step 2: fetch custom role if custom_role_id exists
  if (staffRow.custom_role_id) {
    const { data: roleRow, error: roleError } = await client
      .from('custom_roles')
      .select('id, name, permissions')
      .eq('id', staffRow.custom_role_id)
      .single()

    if (!roleError && roleRow) {
      // Parse permissions — handle both array and JSON string
      let perms: string[] = []
      if (Array.isArray(roleRow.permissions)) {
        perms = roleRow.permissions as string[]
      } else if (typeof roleRow.permissions === 'string') {
        try {
          perms = JSON.parse(roleRow.permissions) as string[]
        } catch {
          perms = []
        }
      }

      if (perms.length > 0) {
        return {
          staff: staffRow,
          permissions: perms,
          roleName: roleRow.name,
        }
      }
    }
  }

  // Step 3: fallback to legacy role name
  const LEGACY_PERMISSIONS: Record<string, string[]> = {
    super_admin: [
      'dashboard',
      'students',
      'enroll',
      'edit_student',
      'placements',
      'analytics',
      'settings',
      'activity',
      'support',
    ],
    editor: ['dashboard', 'students', 'enroll', 'edit_student', 'activity'],
    placements: ['dashboard', 'students_read', 'placements'],
    ceo: [
      'dashboard',
      'students_read',
      'analytics',
      'support',
      'students',
      'enroll',
      'edit_student',
      'placements',
      'settings',
      'activity',
    ],
  }

  const legacyRole = staffRow.role as string
  if (legacyRole && LEGACY_PERMISSIONS[legacyRole]) {
    return {
      staff: staffRow,
      permissions: LEGACY_PERMISSIONS[legacyRole],
      roleName: legacyRole,
    }
  }

  // Nothing worked
  throw new Error('Staff role configuration is missing. Run custom_roles migration in Supabase.')
}

export async function getStaffById(id: string, client: DbClient = defaultDbClient): Promise<StaffRow | null> {
  const { data, error } = await client
    .from('staff')
    .select('*')
    .eq('id', id)
    .or('active.is.null,active.eq.true')
    .maybeSingle()

  if (error) throw error
  return data
}

export async function getStaffByEmail(email: string, client: DbClient = defaultDbClient): Promise<StaffRow | null> {
  const trimmed = email.trim()
  const normalized = trimmed.toLowerCase()

  async function fetchByEmail(value: string, client: DbClient = defaultDbClient): Promise<StaffRow | null> {
    const { data, error } = await client
      .from('staff')
      .select('*')
      .eq('email', value)
      .or('active.is.null,active.eq.true')
      .maybeSingle()
    if (error) throw error
    return data
  }

  return (await fetchByEmail(normalized, client)) ?? (normalized !== trimmed ? await fetchByEmail(trimmed, client) : null)
}

export async function getStaffForAuthUser(user: AuthUserLookup, client: DbClient = defaultDbClient): Promise<StaffRow | null> {
  let staffData = await getStaffById(user.id, client)

  if (!staffData && user.email) {
    staffData = await getStaffByEmail(user.email)
  }

  return staffData
}

export async function getStaffMembers(client: DbClient = defaultDbClient): Promise<StaffRow[]> {
  const { data, error } = await client
    .from('staff')
    .select('*')
    .order('created_at', { ascending: false, nullsFirst: false })
  if (error) throw error
  return data ?? []
}

export interface CreateStaffMemberPayload {
  name: string
  email: string
  customRoleId: string
}

export async function createStaffMember(
  payload: CreateStaffMemberPayload,
  _actor: ActivityActor
): Promise<{ staff: StaffRow; tempPassword: string }> {
  const res = await callHubStaffEdgeFunction('hub-create-staff', {
    name: payload.name.trim(),
    email: payload.email.trim().toLowerCase(),
    customRoleId: payload.customRoleId,
  })

  const json = (await res.json().catch(() => ({}))) as {
    staff?: StaffRow
    tempPassword?: string
    error?: string
  }

  if (!res.ok || !json.staff || !json.tempPassword) {
    throw new Error(json.error ?? 'Failed to create staff member')
  }

  return { staff: json.staff, tempPassword: json.tempPassword }
}

export async function updateStaffCustomRole(id: string, customRoleId: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<StaffRow> {
  const { data: roleRow, error: roleErr } = await client.from('custom_roles').select('id,name').eq('id', customRoleId).maybeSingle()
  if (roleErr) throw roleErr
  if (!roleRow) throw new Error('Selected role was not found')

  const { data: prevRow, error: prevErr } = await client.from('staff').select('*').eq('id', id).maybeSingle()
  if (prevErr) throw prevErr
  if (prevRow?.role === 'super_admin' && roleRow.name !== 'super_admin') {
    throw new Error('Cannot change role for super_admin')
  }

  const { data, error } = await client
    .from('staff')
    .update({ custom_role_id: roleRow.id, role: roleRow.name })
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_STAFF_ROLE',
      entityType: 'staff',
      entityId: id,
      oldValues: prevRow ? { role: prevRow.role, custom_role_id: prevRow.custom_role_id } : undefined,
      newValues: { role: data.role, custom_role_id: data.custom_role_id },
    }), client
  )

  return data
}

export async function deactivateStaff(id: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { data: prevRow, error: prevErr } = await client.from('staff').select('*').eq('id', id).maybeSingle()
  if (prevErr) throw prevErr

  const { error } = await client.from('staff').update({ active: false }).eq('id', id)
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'DEACTIVATE_STAFF',
      entityType: 'staff',
      entityId: id,
      oldValues: prevRow ? { active: prevRow.active } : undefined,
      newValues: { active: false },
    }), client
  )
}

